// ItemList - displays workspace items with state indicators
// Implements US-3.1: Item CRUD and Drag-Drop Reordering
// Implements US-3.2: RICE Scoring Interface and Calculation
// Implements US-3.3: Matrix Scoring Frameworks (Value/Effort, Eisenhower, Risk Assessment)
// Implements US-3.4: MoSCoW Categorical Selection

import React, { useState } from 'react';
import styles from './ItemList.module.css';
import { Item, Framework, Score, CalculatedResult } from '../types';
import { RiceScoring } from './RiceScoring';
import { ValueEffortScoring } from './ValueEffortScoring';
import { EisenhowerScoring } from './EisenhowerScoring';
import { RiskAssessmentScoring } from './RiskAssessmentScoring';
import { MoscowScoring } from './MoscowScoring';

interface ItemListProps {
  items: Item[];
  framework?: Framework;
  isReadOnly: boolean;
  onEditItem?: (itemId: string, newTitle: string) => Promise<void>;
  onDeleteItem?: (itemId: string) => void;
  onReorderItems?: (items: Item[]) => void;
  onScoresUpdated?: (itemId: string, scores: Score[], result: CalculatedResult) => void;
}

/**
 * RENDER list of workspace items with state indicators
 * - Shows item state badges (new/scored)
 * - Supports inline editing (if not read-only)
 * - Supports drag-drop reordering (if not read-only)
 * - Supports deletion (if not read-only)
 */
export const ItemList: React.FC<ItemListProps> = ({
  items,
  framework,
  isReadOnly,
  onEditItem,
  onDeleteItem,
  onReorderItems,
  onScoresUpdated,
}) => {
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // DRAG-AND-DROP state
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [draggedOverIndex, setDraggedOverIndex] = useState<number | null>(null);

  const MAX_LENGTH = 200;
  // RENDER empty state
  if (items.length === 0) {
    return (
      <div className={styles.emptyState}>
        <p>Noch keine Themen / Elemente</p>
        <p className={styles.emptyHint}>
          {isReadOnly
            ? 'Der/Die Workspace-Ersteller:in wird Themen / Elemente hinzufügen'
            : 'Füge oben dein erstes Thema / Element hinzu, um loszulegen'}
        </p>
      </div>
    );
  }

  /**
   * START editing an item
   */
  const startEditing = (item: Item) => {
    setEditingItemId(item.id);
    setEditValue(item.title);
  };

  /**
   * CANCEL editing
   */
  const cancelEditing = () => {
    setEditingItemId(null);
    setEditValue('');
    setIsSubmitting(false);
  };

  /**
   * SAVE edited item
   */
  const saveEdit = async (itemId: string) => {
    const trimmedValue = editValue.trim();

    if (!trimmedValue) {
      cancelEditing();
      return;
    }

    if (trimmedValue.length > MAX_LENGTH) {
      return;
    }

    if (!onEditItem) {
      cancelEditing();
      return;
    }

    try {
      setIsSubmitting(true);
      await onEditItem(itemId, trimmedValue);
      cancelEditing();
    } catch (err) {
      console.error('Failed to save edit:', err);
      setIsSubmitting(false);
    }
  };

  /**
   * HANDLE edit input key events
   */
  const handleEditKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, itemId: string) => {
    if (e.key === 'Enter') {
      saveEdit(itemId);
    } else if (e.key === 'Escape') {
      cancelEditing();
    }
  };

  /**
   * FORMAT item state badge
   */
  const getStateBadge = (state: 'new' | 'scored') => {
    return (
      <span className={`${styles.stateBadge} ${styles[state]}`}>
        {state === 'new' ? 'Nicht bewertet' : 'Bewertet'}
      </span>
    );
  };

  /**
   * HANDLE drag start
   */
  const handleDragStart = (e: React.DragEvent<HTMLLIElement>, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    // Set transparent drag image
    const img = new Image();
    img.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
    e.dataTransfer.setDragImage(img, 0, 0);
  };

  /**
   * HANDLE drag over
   */
  const handleDragOver = (e: React.DragEvent<HTMLLIElement>, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    if (draggedIndex === null || draggedIndex === index) {
      return;
    }

    setDraggedOverIndex(index);
  };

  /**
   * HANDLE drag leave
   */
  const handleDragLeave = () => {
    setDraggedOverIndex(null);
  };

  /**
   * HANDLE drop
   */
  const handleDrop = (e: React.DragEvent<HTMLLIElement>, dropIndex: number) => {
    e.preventDefault();

    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDraggedOverIndex(null);
      return;
    }

    // REORDER items array
    const reorderedItems = [...items];
    const [draggedItem] = reorderedItems.splice(draggedIndex, 1);

    // SAFETY check (should always exist if draggedIndex is valid)
    if (!draggedItem) return;

    reorderedItems.splice(dropIndex, 0, draggedItem);

    // UPDATE order field for each item
    const itemsWithNewOrder = reorderedItems.map((item, index) => ({
      ...item,
      order: index,
    }));

    // CALL parent handler to persist changes
    if (onReorderItems) {
      onReorderItems(itemsWithNewOrder);
    }

    setDraggedIndex(null);
    setDraggedOverIndex(null);
  };

  /**
   * HANDLE drag end
   */
  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDraggedOverIndex(null);
  };

  return (
    <div className={styles.itemListContainer}>
      <ul className={styles.itemList}>
        {items.map((item, index) => (
          <li
            key={item.id}
            className={`${styles.itemCard} ${
              draggedIndex === index ? styles.dragging : ''
            } ${draggedOverIndex === index ? styles.dragOver : ''}`}
            draggable={!isReadOnly}
            onDragStart={(e) => handleDragStart(e, index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, index)}
            onDragEnd={handleDragEnd}
          >
            {/* ITEM number */}
            <div className={styles.itemNumber}>{index + 1}</div>

            {/* ITEM content */}
            <div className={styles.itemContent}>
              <div className={styles.itemHeader}>
                {/* INLINE editing mode */}
                {editingItemId === item.id ? (
                  <div className={styles.editWrapper}>
                    <input
                      type="text"
                      className={styles.editInput}
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onKeyDown={(e) => handleEditKeyDown(e, item.id)}
                      onBlur={() => saveEdit(item.id)}
                      disabled={isSubmitting}
                      autoFocus
                      maxLength={MAX_LENGTH}
                    />
                    <span className={styles.editHint}>
                      Enter zum Speichern, Esc zum Abbrechen
                    </span>
                  </div>
                ) : (
                  <>
                    <span className={styles.itemTitle}>{item.title}</span>

                    {/* ACTION buttons - only visible for users (not viewers) */}
                    {!isReadOnly && (
                      <div className={styles.itemActions}>
                        <button
                          className={styles.actionButton}
                          onClick={() => startEditing(item)}
                          title="Thema / Element bearbeiten"
                        >
                          Bearbeiten
                        </button>
                        <button
                          className={`${styles.actionButton} ${styles.deleteButton}`}
                          onClick={() => onDeleteItem && onDeleteItem(item.id)}
                          title="Thema / Element löschen"
                        >
                          Löschen
                        </button>
                      </div>
                    )}

                    {/* STATE badge on far right */}
                    {getStateBadge(item.state)}
                  </>
                )}
              </div>

              {/* SCORING PANEL - conditional based on framework */}
              {framework && framework.slug === 'rice' && (
                <RiceScoring
                  item={item}
                  isReadOnly={isReadOnly}
                  {...(onScoresUpdated && { onScoresUpdated })}
                />
              )}

              {framework && framework.slug === 'value-effort' && (
                <ValueEffortScoring
                  item={item}
                  isReadOnly={isReadOnly}
                  {...(onScoresUpdated && { onScoresUpdated })}
                />
              )}

              {framework && framework.slug === 'eisenhower' && (
                <EisenhowerScoring
                  item={item}
                  isReadOnly={isReadOnly}
                  {...(onScoresUpdated && { onScoresUpdated })}
                />
              )}

              {framework && framework.slug === 'risk-assessment' && (
                <RiskAssessmentScoring
                  item={item}
                  isReadOnly={isReadOnly}
                  {...(onScoresUpdated && { onScoresUpdated })}
                />
              )}

              {framework && framework.slug === 'moscow' && (
                <MoscowScoring
                  item={item}
                  isReadOnly={isReadOnly}
                  {...(onScoresUpdated && { onScoresUpdated })}
                />
              )}
            </div>

            {/* DRAG handle - only visible for users (not viewers) */}
            {!isReadOnly && (
              <div className={styles.dragHandle} title="Ziehen zum Umsortieren">
                ⋮⋮
              </div>
            )}
          </li>
        ))}
      </ul>

      {/* ITEM count indicator */}
      <div className={styles.itemCount}>
        {items.length === 1 ? items.length : `${items.length} Themen / Elemente`}
      </div>
    </div>
  );
};
